import React from 'react';
import { MapPin, Phone, ArrowRight, CheckCircle, Building2, Clock, Shield, Trees } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const BaltimoreCountyPage = () => {
  const services = [
    { name: 'Outpatient Therapy (OMHC)', desc: 'Evidence-based individual and group therapy for anxiety, depression, trauma, and other behavioral health conditions.', link: '/services/outpatient-mental-health' },
    { name: 'Psychiatric Rehabilitation (PRP)', desc: 'Community-based rehabilitation supporting daily living skills, employment readiness, and social reintegration.', link: '/services/psychiatric-rehabilitation' },
    { name: 'Medication Management', desc: 'Psychiatric evaluations and ongoing medication monitoring tailored to your treatment goals.', link: '/services/medication-management' },
    { name: 'Case Management', desc: 'Personalized coordination connecting you with housing, benefits, healthcare, and community programs.', link: '/services/case-management' },
    { name: 'Comprehensive Assessment', desc: 'Detailed clinical evaluations for accurate diagnosis and treatment recommendations.', link: '/services/comprehensive-assessment' },
    { name: 'Telehealth Services', desc: 'Convenient virtual appointments for therapy and medication management across Maryland.', link: '/services/telehealth' },
  ];

  const neighborhoods = [
    'Pikesville', 'Owings Mills', 'Randallstown', 'Reisterstown',
    'Towson', 'Catonsville', 'Woodlawn', 'Dundalk',
    'Cockeysville', 'Perry Hall', 'Essex', 'Parkville',
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Mental Health Services in Baltimore County"
        description="Mental health services in Baltimore County, MD. SDORA Wellbeing offers outpatient therapy, PRP, medication management, and case management in Pikesville serving all of Baltimore County. CARF accredited. Accepting Medicaid."
        path="/areas/baltimore-county"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "SDORA Wellbeing LLC — Baltimore County Services",
          "description": "Outpatient mental health therapy, psychiatric rehabilitation, medication management, and case management serving Baltimore County residents from our Pikesville office.",
          "url": "https://sdorawellbeing.org/areas/baltimore-county",
          "telephone": "+1-877-772-5150",
          "image": "https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/sthkmul7_SDORA_Logo-copy.png",
          "priceRange": "$$",
          "areaServed": { "@type": "AdministrativeArea", "name": "Baltimore County", "containedInPlace": { "@type": "State", "name": "Maryland" } },
          "address": { "@type": "PostalAddress", "streetAddress": "1700 Reisterstown Rd, Suite 109", "addressLocality": "Pikesville", "addressRegion": "MD", "postalCode": "21208", "addressCountry": "US" }
        }}
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-800 to-teal-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MapPin className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Mental Health Services in Baltimore County
          </h1>
          <p className="text-lg text-emerald-200 max-w-2xl mx-auto">
            Trusted behavioral health care for Baltimore County residents. Our Pikesville office serves communities across the county with in-person and telehealth options.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <section className="mb-12" data-testid="baltimore-county-intro">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Your Local Behavioral Health Provider</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            SDORA Wellbeing LLC is based in the heart of Baltimore County at 1700 Reisterstown Road in Pikesville.
            We serve adults and youth from communities across the county, including Owings Mills, Randallstown,
            Reisterstown, Towson, Catonsville, Woodlawn, and beyond.
          </p>
          <p className="text-gray-700 leading-relaxed">
            As a CARF-accredited organization, we maintain the highest standards of care while keeping services
            accessible and community-focused. Whether you need therapy, psychiatric rehabilitation, medication management,
            or coordinated case management, our team is ready to support your mental health journey.
          </p>
        </section>

        {/* Services Grid */}
        <section className="mb-12" data-testid="baltimore-county-services">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Services for Baltimore County Residents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((svc) => (
              <div key={svc.name} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-900 text-sm mb-2">{svc.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{svc.desc}</p>
                <Link to={svc.link} className="text-sm text-emerald-600 font-medium hover:underline inline-flex items-center gap-1">
                  Learn more <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Communities We Serve */}
        <section className="mb-12" data-testid="baltimore-county-communities">
          <div className="flex items-start gap-3 mb-4">
            <Trees className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Communities We Serve</h2>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            Our Pikesville office is centrally located and accessible to residents across Baltimore County, including:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {neighborhoods.map((area) => (
              <div key={area} className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{area}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Telehealth services are available to eligible individuals throughout Maryland, not limited to Baltimore County.
          </p>
        </section>

        {/* Location & Access */}
        <section className="mb-12 bg-emerald-50 rounded-xl p-6 border border-emerald-100" data-testid="baltimore-county-location">
          <div className="flex items-start gap-3 mb-4">
            <Building2 className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Our Office</h2>
          </div>
          <div className="space-y-3 text-sm text-gray-700">
            <p><strong>Address:</strong> 1700 Reisterstown Rd, Suite 109, Pikesville, MD 21208</p>
            <p><strong>Accessibility:</strong> Located on the Reisterstown Road corridor with direct access from I-695 (Baltimore Beltway). MTA bus service available along Reisterstown Road.</p>
            <p><strong>Parking:</strong> Free on-site parking available for all visitors.</p>
          </div>
        </section>

        {/* Hours */}
        <section className="mb-12" data-testid="baltimore-county-hours">
          <div className="flex items-start gap-3 mb-4">
            <Clock className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Office Hours</h2>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
              <span className="font-medium">Monday – Friday</span><span>9:00 AM – 5:00 PM</span>
              <span className="font-medium">Saturday – Sunday</span><span>Closed</span>
            </div>
            <p className="text-xs text-gray-500 mt-3">Evening and weekend telehealth appointments may be available. Call us for scheduling flexibility.</p>
          </div>
        </section>

        {/* Conditions We Treat */}
        <section className="mb-12" data-testid="baltimore-county-conditions">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Conditions We Treat</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {[
              'Depression', 'Anxiety Disorders', 'PTSD & Trauma',
              'Bipolar Disorder', 'Schizophrenia', 'ADHD',
              'Anger Management', 'Grief & Loss', 'Adjustment Disorders',
            ].map((condition) => (
              <div key={condition} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{condition}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Insurance */}
        <section className="mb-12 bg-teal-50 rounded-xl p-6 border border-teal-100" data-testid="baltimore-county-insurance">
          <div className="flex items-start gap-3 mb-3">
            <Shield className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Insurance & Medicaid</h2>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            We accept Medicaid, Maryland HealthChoice MCOs (Aetna Better Health, CareFirst Community, Maryland Physicians Care,
            Priority Partners, UnitedHealthcare, and others), Medicare, and most commercial insurance plans.
            Baltimore County residents enrolled in Medical Assistance are welcome.
          </p>
          <Link to="/insurance" className="text-sm text-teal-700 font-semibold hover:underline inline-flex items-center gap-1">
            View all accepted insurance plans <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* Related Resources */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Helpful Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { name: 'Depression Treatment', link: '/resources/depression-treatment' },
              { name: 'Anxiety Treatment', link: '/resources/anxiety-treatment' },
              { name: 'PTSD Support', link: '/resources/ptsd-support' },
            ].map((res) => (
              <Link key={res.name} to={res.link} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors">
                <ArrowRight className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700">{res.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200" data-testid="baltimore-county-cta">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Connect with Care in Baltimore County</h2>
          <p className="text-sm text-gray-600 mb-4">
            Your mental health matters. Call us today or submit a referral to begin your path toward recovery and well-being.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+18777725150" className="inline-flex items-center gap-2 bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-800 transition-colors">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <Link to="/get-started" className="inline-flex items-center gap-2 bg-white text-emerald-700 border border-emerald-200 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default BaltimoreCountyPage;
